import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { authApi } from "../../api";
import { useAuth } from "../../utils/auth";

const OtpContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9f9;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const OtpCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 32px 16px;
    gap: 20px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 590;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  margin: 0 0 4px 0;
  letter-spacing: 0.352px;
  width: 100%;
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  margin: 0;
  letter-spacing: -0.154px;
  width: 100%;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  margin-bottom: 8px;
  letter-spacing: -0.0004px;
`;

const OtpInput = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  padding: 10px 12px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  color: #2f3941;
  letter-spacing: 2px;
  text-align: center;
  
  &::placeholder {
    color: #68737d;
    letter-spacing: normal;
  }
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &.error {
    border-color: #cc3340;
  }
`;

const VerifyButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: #d8dcde;
    cursor: not-allowed;
  }
`;

const ResendButton = styled.button`
  background: none;
  color: #8d4f1b;
  border: none;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #703815;
  }
  
  &:disabled {
    color: #68737d;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const ErrorMessage = styled.div`
  color: #cc3340;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SuccessMessage = styled.div`
  color: #00a86b;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const OtpVerification: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Get email and password from location state or redirect to signup
  const email = location.state?.email;
  const password = location.state?.password;
  const contextMessage = location.state?.message;
  
  React.useEffect(() => {
    if (!email) {
      navigate("/signup");
    }
  }, [email, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp) {
      setError("Please enter the OTP code");
      return;
    }
    
    if (otp.length !== 6) {
      setError("OTP must be 6 digits");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      console.log('🔄 Verifying OTP for email:', email);
      const response = await authApi.verifyOtp(email, otp);
      console.log('✅ OTP verification response:', response);
      if (response?.message === 'Email verified successfully') {
        console.log('✅ Email verified successfully');
        // If we have the password, automatically log in the user
        if (password) {
          console.log('🔄 Attempting auto-login with provided password...');
          try {
            const authResponse = await login(email, password);
            const { user } = authResponse;
            console.log('✅ Auto-login successful, user role:', user.role);
            // Navigate based on user role
            switch (user.role) {
              case 'physician':
                console.log('🧭 Navigating to physician dashboard');
                navigate("/physician");
                break;
              case 'specialist':
                console.log('🧭 Navigating to reviewer dashboard');
                navigate("/reviewer");
                break;
              case 'admin':
              case 'super-admin':
                console.log('🧭 Navigating to admin dashboard');
                navigate("/admin");
                break;
              default:
                console.log('🧭 Navigating to home');
                navigate("/");
            }
          } catch (loginError: any) {
            console.error('❌ Auto-login failed:', loginError);
            // If auto-login fails, redirect to login page
            console.log('🧭 Redirecting to login page due to auto-login failure');
            navigate("/login", { 
              state: { 
                email,
                message: "Account verified successfully! Please sign in." 
              } 
            });
          }
        } else {
          // No password available, redirect to login
          console.log('🧭 No password available, redirecting to login page');
          navigate("/login", { 
            state: { 
              email,
              message: "Account verified successfully! Please sign in." 
            } 
          });
        }
      } else {
        console.log('❌ Unexpected verification response:', response);
        setError('Verification failed. Please try again.');
      }
    } catch (err: any) {
      console.error('❌ OTP verification failed:', err);
      setError(err.message || "Invalid OTP code");
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError("");
    setResendMessage("");
    
    try {
      await authApi.resendOtp(email);
      setResendMessage("OTP resent successfully!");
      setTimeout(() => setResendMessage(""), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to resend OTP");
    }
    
    setResendLoading(false);
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      if (error) {
        setError("");
      }
    }
  };

  if (!email) {
    return null; // Will redirect to signup
  }

  return (
    <OtpContainer>
      <OtpCard>
        <TitleSection>
          <Title>Verify Your Email</Title>
          <Subtitle>
            {contextMessage || `We've sent a 6-digit verification code to ${email}. Please enter it below to complete your registration.`}
          </Subtitle>
        </TitleSection>
        
        <FormContainer onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="otp">Verification Code</Label>
            <OtpInput
              id="otp"
              type="text"
              placeholder="000000"
              value={otp}
              onChange={handleOtpChange}
              className={error ? "error" : ""}
              maxLength={6}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {resendMessage && <SuccessMessage>{resendMessage}</SuccessMessage>}
          </InputGroup>
          
          <VerifyButton type="submit" disabled={loading}>
            {loading ? "Verifying..." : "Verify Account"}
          </VerifyButton>
        </FormContainer>
        
        <div style={{ textAlign: 'center' }}>
          <span style={{ 
            fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: '14px',
            color: '#68737d',
            marginRight: '8px'
          }}>
            Didn't receive the code?
          </span>
          <ResendButton 
            type="button" 
            onClick={handleResendOtp}
            disabled={resendLoading}
          >
            {resendLoading ? "Sending..." : "Resend OTP"}
          </ResendButton>
        </div>
      </OtpCard>
    </OtpContainer>
  );
};

export default OtpVerification;
